import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { useCopilot } from "@/context/CopilotContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { X, Loader2, Phone, Play, Clock, UserCog, GitBranch, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { SmsStatusPanel } from "@/components/SmsStatusPanel";

const EVENT_ICON = { call: Phone, activity: Clock, restricted_access: ShieldCheck };

function RecordingButton({ callId, status }) {
  const [state, setState] = useState("idle");
  const [url, setUrl] = useState(null);
  const play = async () => {
    setState("loading");
    try {
      const res = await api.get(`/voice/calls/${callId}/recording`, { responseType: "blob" });
      setUrl(URL.createObjectURL(res.data));
      setState("ready");
    } catch (e) {
      const code = e?.response?.status;
      setState("error");
      if (code === 202) toast.info("Recording is still processing — try again shortly.");
      else if (code === 409) toast.error("Recording failed and is unavailable.");
      else if (code === 404) toast.info("No recording available for this call.");
      else if (code === 403) toast.error("You are not authorized to play this recording.");
      else toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };
  if (status !== "ready") {
    return <span className="text-[11px] text-muted-foreground" data-testid={`rec-state-${callId}`}>
      {status === "processing" ? "Recording processing…" : status === "failed" ? "Recording failed" : "No recording"}
    </span>;
  }
  if (url) return <audio controls src={url} className="h-8" data-testid={`rec-audio-${callId}`} />;
  return (
    <Button size="sm" variant="outline" onClick={play} disabled={state === "loading"} data-testid={`rec-play-${callId}`}>
      {state === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Play className="mr-1 h-3.5 w-3.5" /> Play</>}
    </Button>
  );
}

export function CaseDrawer({ leadId, onClose, canManage }) {
  const { setActiveEntity } = useCopilot();
  const [tl, setTl] = useState(null);
  const [calls, setCalls] = useState([]);
  const [vocab, setVocab] = useState({ lead: [], application: [], servicing: [] });
  const [newStage, setNewStage] = useState("");
  const [assignee, setAssignee] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setActiveEntity({ id: leadId, type: "lead" });
    return () => setActiveEntity(null);
  }, [leadId, setActiveEntity]);

  const load = useCallback(() => {
    api.get(`/workflow/timeline/${leadId}`).then(({ data }) => setTl(data)).catch(() => setTl({ error: true }));
    api.get(`/voice/calls?lead_id=${leadId}`).then(({ data }) => setCalls(data)).catch(() => setCalls([]));
    api.get(`/workflow/vocab`).then(({ data }) => setVocab(data)).catch(() => {});
  }, [leadId]);
  useEffect(load, [load]);

  const doTransition = async () => {
    if (!newStage) return;
    setBusy(true);
    try {
      await api.post(`/workflow/leads/${leadId}/transition`, { stage: newStage });
      toast.success(`Moved to ${newStage}`); setNewStage(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doAssign = async () => {
    if (!assignee.trim()) return;
    setBusy(true);
    try {
      await api.post(`/workflow/leads/${leadId}/assign`, { assigned_agent: assignee.trim(), reason: "manual" });
      toast.success("Reassigned"); setAssignee(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const allStages = [...vocab.lead, ...vocab.application, ...vocab.servicing];

  return (
    <div className="fixed inset-0 z-[70] flex justify-end bg-black/50" data-testid="case-drawer" onClick={onClose}>
      <div className="h-full w-full max-w-lg overflow-y-auto bg-background p-5 shadow-xl kanban-scroll" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-lg font-semibold">Case Timeline</div>
            {tl && !tl.error && (
              <div className="mt-1 text-xs text-muted-foreground">
                {tl.case_ref ? `Case ${tl.case_ref} · ` : ""}
                <span className="rounded bg-[#17324D]/10 px-1.5 py-0.5 font-medium text-[#17324D]">
                  {tl.canonical?.kind} · {tl.canonical?.canonical}
                </span>
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="case-drawer-close" className="rounded p-1 hover:bg-muted"><X className="h-5 w-5" /></button>
        </div>

        {canManage && (
          <div className="mb-4 space-y-2 rounded-lg border bg-card p-3">
            <div className="flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-muted-foreground" />
              <Select value={newStage} onValueChange={setNewStage}>
                <SelectTrigger className="h-8 flex-1" data-testid="case-stage-select"><SelectValue placeholder="Move to stage…" /></SelectTrigger>
                <SelectContent>{allStages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" onClick={doTransition} disabled={busy || !newStage} data-testid="case-transition-btn">Apply</Button>
            </div>
            <div className="flex items-center gap-2">
              <UserCog className="h-4 w-4 text-muted-foreground" />
              <Input className="h-8 flex-1" placeholder="Reassign to agent name" value={assignee}
                onChange={(e) => setAssignee(e.target.value)} data-testid="case-assign-input" />
              <Button size="sm" variant="outline" onClick={doAssign} disabled={busy || !assignee} data-testid="case-assign-btn">Assign</Button>
            </div>
          </div>
        )}

        <SmsStatusPanel leadId={leadId} canRetry={canManage} />

        {calls.length > 0 && (
          <div className="mb-4 rounded-lg border bg-card p-3">
            <div className="mb-2 text-sm font-semibold">Calls & Recordings</div>
            <div className="space-y-2">
              {calls.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`case-call-${c.id}`}>
                  <div>
                    <div className="font-medium">{c.display_id} · {c.direction} · {c.status}</div>
                    <div className="text-muted-foreground">{c.to_number} {c.disposition ? `· ${c.disposition}` : ""}</div>
                  </div>
                  <RecordingButton callId={c.id} status={c.recording_status || "none"} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-lg border bg-card p-3">
          <div className="mb-2 text-sm font-semibold">Activity</div>
          {!tl ? <Loader2 className="h-4 w-4 animate-spin" /> : tl.error ? (
            <div className="text-xs text-muted-foreground">Unable to load timeline.</div>
          ) : tl.events.length === 0 ? (
            <div className="text-xs text-muted-foreground">No activity yet.</div>
          ) : (
            <ol className="space-y-3" data-testid="case-timeline-events">
              {tl.events.map((e, i) => {
                const Icon = EVENT_ICON[e.type] || Clock;
                return (
                  <li key={i} className="flex gap-2 text-xs">
                    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#B28A3D]" />
                    <div>
                      <div>{e.text}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {e.actor || "System"} · {e.ts ? new Date(e.ts).toLocaleString() : ""}
                        {e.recording_available ? " · recording ready" : ""}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
